import React, {useEffect, useState} from 'react'
import {Grid, Stack, CircularProgress, Box, Typography} from '@mui/material'
import {DatePicker, LocalizationProvider, TimePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import groupCyclesApi from '../../../services/api/surveys/groupCyclesApi'
import surveysApi from '../../../services/api/surveys/surveysApi'
import WbTextField from '../../../components/common/WbTextField'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import WbButton from '../../../components/common/WbButton'
import WbAlert from '../../../components/common/WbAlert'
import {getApiHookParams} from '../../../auth'

const LIVE_STATUS = 3

/**
 * Normalize API date string to ISO.
 * Handles: "2026-04-29 19:00:00", "2026-04-29T19:00:00.000Z", etc.
 */
function normalizeApiDateToISO(dateStr) {
  if (!dateStr) return null
  let s = String(dateStr).trim()
  if (!s) return null
  s = s.replace(' ', 'T')
  if (!/[Zz]$|[-+]\d{2}:?\d{2}$/.test(s)) {
    s += 'Z'
  }
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

function formatDisplayDateTime(isoStr, endHour, endMin) {
  if (!isoStr) return '—'
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return '—'
  const hour = endHour != null ? endHour : d.getUTCHours()
  const min = endMin != null ? endMin : d.getUTCMinutes()
  const datePart = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const timePart = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  return `${datePart}, ${timePart}`
}

export default function ChangeEndDate({survey, onClose, onSuccess}) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [liveCycle, setLiveCycle] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const apiParams = getApiHookParams()

  useEffect(() => {
    setLoading(true)
    groupCyclesApi
      .getBySurveyToken(survey.token)
      .then(res => {
        const cycles = res.groupCycles || []
        cycles.forEach(cycle => {
          if (cycle.endDate) {
            const normalized = normalizeApiDateToISO(cycle.endDate)
            if (normalized) cycle.endDate = normalized
          }
        })
        const live = cycles.find(c => c.status === LIVE_STATUS)
        setLiveCycle(live || null)
        if (live) {
          const endDateVal = live.endDate ? dayjs(live.endDate) : null
          setEndDate(endDateVal)
          const hour = live.endHour != null ? live.endHour : (endDateVal ? endDateVal.hour() : 0)
          const min = live.endMin != null ? live.endMin : (endDateVal ? endDateVal.minute() : 0)
          setEndTime(dayjs().hour(hour).minute(min).second(0).millisecond(0))
        }
      })
      .catch(() => setLiveCycle(null))
      .finally(() => setLoading(false))
  }, [survey.token])

  const handleUpdate = () => {
    if (!liveCycle || !endDate || !endTime) return
    setSubmitting(true)
    const hour = endTime.hour()
    const min = endTime.minute()
    const combined = endDate.hour(hour).minute(min).second(0).millisecond(0)
    const endDateISO = combined.toISOString()

    const payload = {
      groupCycleId: liveCycle.id,
      endDate: endDateISO,
      endHour: hour,
      endMin: min,
      groupId: apiParams.groupId ?? 0,
      schoolId: survey.schoolId ?? apiParams.schoolId ?? 0,
    }

    surveysApi
      .updateLiveSurveyEndDate(payload)
      .then(() => {
        WbAlert({message: 'End date updated successfully', type: 'success'})
        onSuccess()
        onClose()
      })
      .catch(() => {
        WbAlert({message: 'Failed to update end date', type: 'error'})
      })
      .finally(() => setSubmitting(false))
  }

  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{py: 3}}>
        <CircularProgress />
      </Grid>
    )
  }

  if (!liveCycle) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          No live survey cycle found for this survey.
        </Grid>
        <Grid item xs={12} style={{display: 'flex', justifyContent: 'flex-end'}}>
          <WbOutlineButton CustomOutlineButtonText="Close" onClick={onClose} />
        </Grid>
      </Grid>
    )
  }

  const currentEndDisplay = formatDisplayDateTime(
    liveCycle.endDate,
    liveCycle.endHour,
    liveCycle.endMin
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: '#E8F4FD',
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Stack spacing={0.75}>
              <Typography component="div" variant="body2">
                <Box component="span" fontWeight={600}>
                  Survey Name:{' '}
                </Box>
                {survey.name}
              </Typography>
              <Typography component="div" variant="body2">
                <Box component="span" fontWeight={600}>
                  End Date/Time:{' '}
                </Box>
                {currentEndDisplay}
              </Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1.5}}>
            Update End Date/Time
          </Typography>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
            <DatePicker
              label="End date"
              value={endDate}
              onChange={setEndDate}
              disablePast
              slots={{textField: WbTextField}}
              slotProps={{textField: {fullWidth: true}}}
            />
            <TimePicker
              label="End time"
              value={endTime}
              onChange={setEndTime}
              ampm={false}
              slots={{textField: WbTextField}}
              slotProps={{textField: {fullWidth: true}}}
            />
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            textAlign: 'right',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <WbOutlineButton
            CustomOutlineButtonText="Cancel"
            onClick={onClose}
            disabled={submitting}
          />
          <WbButton
            CustomButtonText="Update"
            onClick={handleUpdate}
            disabled={submitting || !endDate || !endTime}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  )
}
